import { useEffect } from "react";
import { useQuery } from "react-query";
import { apiClient } from "../api/client";
import i18n from "../i18n/config";
import { useAuthContext } from "../context/AuthContext";

const DEFAULT_NAMESPACES = [
  "common",
  "app",
  "errors",
  "auth",
  "dataGrid",
  "profile",
  "settings_i18n",
  "themes",
  "shell",
  "chatList",
];

function bundleStorageKey(company, lng) {
  return `i18n_bundle_meta_${company || "na"}_${lng}`;
}

/**
 * Busca traduções dinâmicas (globais + overrides de workspace) e faz merge no i18next.
 * Suporta If-None-Match / 304 e cache em sessionStorage.
 */
export function useRemoteI18nBundle() {
  const { bearerToken, userData } = useAuthContext();
  const company =
    typeof localStorage !== "undefined" ? localStorage.getItem("company") : null;
  const lng =
    String(i18n.language || "").trim() ||
    (userData?.locale_code && String(userData.locale_code).trim()) ||
    "pt-BR";

  const query = useQuery(
    ["i18n-bundle", company, lng],
    async () => {
      const cdnBase =
        typeof process !== "undefined" && process.env.REACT_APP_I18N_CDN_BASE
          ? String(process.env.REACT_APP_I18N_CDN_BASE).replace(/\/+$/, "")
          : "";

      if (cdnBase) {
        try {
          const url = `${cdnBase}/${encodeURIComponent(lng)}/core.json`;
          const r = await fetch(url, { method: "GET" });
          if (r.ok) {
            const json = await r.json();
            if (json && typeof json === "object" && json.namespaces) {
              return { data: json, fromCdn: true };
            }
          }
        } catch {
          /* fallback API */
        }
      }

      const sk = bundleStorageKey(company, lng);
      let etag = "";
      try {
        const raw = sessionStorage.getItem(sk);
        if (raw) etag = JSON.parse(raw).etag || "";
      } catch {
        /* ignore */
      }

      const res = await apiClient.request({
        url: "/i18n/bundle",
        method: "GET",
        params: { locale: lng, namespaces: DEFAULT_NAMESPACES.join(",") },
        headers: etag ? { "If-None-Match": etag } : {},
        validateStatus: (s) => s === 200 || s === 304,
      });

      if (res.status === 304) {
        return { notModified: true };
      }

      const data = res.data?.data;
      const newEtag = res.headers?.etag || res.headers?.ETag || "";
      if (newEtag && data) {
        try {
          sessionStorage.setItem(
            sk,
            JSON.stringify({
              etag: newEtag,
              catalogVersion: data.catalogVersion,
            })
          );
        } catch {
          /* ignore */
        }
      }
      return { data, etag: newEtag };
    },
    {
      enabled: !!bearerToken && !!company,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (!query.data || query.data.notModified) return;
    const data = query.data.data;
    if (!data?.namespaces) return;
    const loc = data.locale || lng;
    for (const [ns, content] of Object.entries(data.namespaces)) {
      if (content && typeof content === "object") {
        i18n.addResourceBundle(loc, ns, content, true, true);
      }
    }
  }, [query.data, lng]);
}
